// @file Network requests related to comment attachment settings from surface

import { asciiSafeStringify } from '@@/bits/json_stringify'
import type { VersionedFetchOptions } from '@@/surface/api/fetchable_object'
import FetchableObject from '@@/surface/api/fetchable_object'
import { fetchJson } from '@@/surface/api_fetch'
import type { CommentAttachmentSettings as CommentAttachmentSettingsType } from '@@/types'
import type { JsonAPIResponse } from '@padlet/arvo'
import { HTTPMethod } from '@padlet/fetch'

class CommentAttachmentSettingsApi extends FetchableObject {
  public static get url(): string {
    return `/api/\${ apiVersion }/walls/\${ wallHashid }/comment-attachment-settings`
  }

  public static async fetch(
    wallHashid: string,
    fetchOptions: VersionedFetchOptions = { apiVersion: 9 },
  ): Promise<CommentAttachmentSettingsType> {
    const { apiVersion, ...otherFetchOptions } = fetchOptions
    const response: JsonAPIResponse<CommentAttachmentSettingsType> = await fetchJson(
      this.buildUrl(this.url, { wallHashid, apiVersion }),
      {
        method: HTTPMethod.get,
        ...otherFetchOptions,
      },
    )
    const settings = Array.isArray(response.data) ? response.data[0]?.attributes : response.data?.attributes
    if (settings == null) throw new Error('Invalid response from server')
    return settings
  }

  public static async update(
    wallHashid: string,
    settings: CommentAttachmentSettingsType,
    fetchOptions: VersionedFetchOptions = { apiVersion: 9 },
  ): Promise<CommentAttachmentSettingsType> {
    const { apiVersion, ...otherFetchOptions } = fetchOptions
    const response: JsonAPIResponse<CommentAttachmentSettingsType> = await fetchJson(
      this.buildUrl(this.url, { wallHashid, apiVersion }),
      {
        method: HTTPMethod.patch,
        body: asciiSafeStringify({ data: { attributes: settings } }),
        ...otherFetchOptions,
      },
    )
    const updatedSettings = Array.isArray(response.data) ? response.data[0]?.attributes : response.data?.attributes
    if (updatedSettings == null) throw new Error('Invalid response from server')
    return updatedSettings
  }
}

export { CommentAttachmentSettingsApi }
